import { z } from "zod";

import type {
  ActivityAdapter,
  ActivityCompletionMetrics
} from "@/app/integration/contracts";
import {
  getForestMarketHintCue,
  getForestMarketRetryCue,
  getMarketItemLabel,
  getMarketItemUnit,
  getNumberTrainHintCue,
  getNumberTrainRetryCue,
  getPatternHintCue,
  getPatternRetryCue,
  MATH_PATTERN_TOKEN_LABELS
} from "@/audio/curriculum-catalog/math-feedback-cues";
import type { CurriculumVoiceCue } from "@/audio/curriculum-catalog/cue";
import type {
  ForestMarketPayload,
  GridDirection,
  GridPosition,
  NumberTrainPayload,
  PatternBridgePayload,
  PatternToken
} from "@/content/math";
import { ACTIVITY_PAYLOAD_SCHEMAS } from "@/content/registry-core";
import {
  mathPatternSequencePayloadSchema
} from "@/features/domain-slices/math/math-pattern-bridge-adapter";
import {
  advanceHintEscalation,
  createRetryFeedback,
  type ActiveHintStage
} from "@/game/feedback";
import type { InputMethod, NavigationDirection } from "@/game/input";
import {
  calculateChoiceMatchCompletionScore,
  completeChoiceMatchProgress,
  createChoiceActivityState,
  reduceChoiceActivity,
  type ChoiceActivityState,
  type SingleChoicePayload
} from "@/game/templates/choice-match";

export { MATH_PATTERN_TOKEN_LABELS };

export const MATH_GRID_DIRECTION_LABELS: Readonly<
  Record<GridDirection, string>
> = {
  up: "上",
  right: "右",
  down: "下",
  left: "左"
};

export type MathPlayerActivityType =
  | "number-train"
  | "forest-market"
  | "pattern-bridge";

export type MathChoiceDisplay =
  | {
      readonly kind: "stars";
      readonly count: number;
    }
  | {
      readonly kind: "market-item";
      readonly count: number;
      readonly itemId: string;
    }
  | {
      readonly kind: "pattern-token";
      readonly token: PatternToken;
    }
  | {
      readonly kind: "route-position";
      readonly position: GridPosition;
    };

export interface MathRenderChoice {
  readonly disabled: boolean;
  readonly display: MathChoiceDisplay;
  readonly focused: boolean;
  readonly id: string;
  readonly label: string;
  readonly selected: boolean;
}

export type MathPlayerVisual =
  | ({ readonly kind: "number-train" } & (
      | {
          readonly mode: "match";
          readonly relation: null;
          readonly targetCount: number;
        }
      | {
          readonly mode: "compare";
          readonly relation: "most" | "fewest";
          readonly targetCount: null;
        }
    ))
  | {
      readonly kind: "forest-market";
      readonly changeCount: number;
      readonly itemId: string;
      readonly itemLabel: string;
      readonly itemUnit: string;
      readonly operation: ForestMarketPayload["operation"];
      readonly startingCount: number;
    }
  | {
      readonly kind: "pattern-sequence";
      readonly tokens: readonly (PatternToken | null)[];
    }
  | {
      readonly columns: number;
      readonly kind: "pattern-route";
      readonly moves: readonly GridDirection[];
      readonly rows: number;
      readonly start: GridPosition;
    };

export interface MathPlayerHintModel {
  readonly audioPath: string;
  readonly kind: "hint" | "retry";
  readonly playbackRequest: number;
  readonly stage: ActiveHintStage;
  readonly text: string;
}

export interface MathPlayerResultModel {
  readonly heading: string;
  readonly message: string;
}

export interface MathPlayerRenderModel {
  readonly activityType: MathPlayerActivityType;
  readonly choicePrompt: string;
  readonly choices: readonly MathRenderChoice[];
  readonly completed: boolean;
  readonly hint: MathPlayerHintModel | null;
  readonly result: MathPlayerResultModel | null;
  readonly visual: MathPlayerVisual;
}

export interface MathPlayerState extends ChoiceActivityState {
  readonly lastAttemptedChoiceId: string | null;
}

export type MathPlayerAction =
  | {
      readonly type: "select-choice";
      readonly choiceId: string;
      readonly inputMethod: InputMethod;
    }
  | {
      readonly type: "move-focus";
      readonly currentChoiceId: string | null;
      readonly direction: NavigationDirection;
    }
  | { readonly type: "activate-focused" }
  | { readonly type: "request-hint" }
  | { readonly type: "replay-prompt" }
  | { readonly type: "reset" };

interface MathChoiceDefinition {
  readonly display: MathChoiceDisplay;
  readonly id: string;
  readonly label: string;
}

interface MathChoiceAdapterConfig<TPayload> {
  readonly activityType: MathPlayerActivityType;
  readonly createVisual: (
    payload: TPayload,
    state: MathPlayerState
  ) => MathPlayerVisual;
  readonly getCanonicalCorrectChoiceId: (payload: TPayload) => string;
  readonly getChoicePrompt: (payload: TPayload) => string;
  readonly getChoices: (
    payload: TPayload
  ) => readonly MathChoiceDefinition[];
  readonly getHintCue: (
    payload: TPayload,
    stage: ActiveHintStage
  ) => CurriculumVoiceCue;
  readonly getRetryCue: (payload: TPayload) => CurriculumVoiceCue;
  readonly getResult: (payload: TPayload) => MathPlayerResultModel;
  readonly isCorrectChoice: (
    payload: TPayload,
    choiceId: string
  ) => boolean;
  readonly parsePayload: (value: unknown) => TPayload;
}

function createChoicePayload<TPayload>(
  config: MathChoiceAdapterConfig<TPayload>,
  payload: TPayload
): SingleChoicePayload {
  return {
    kind: "single-choice",
    choices: config.getChoices(payload).map(({ id, label }) => ({
      id,
      label
    })),
    correctChoiceId: config.getCanonicalCorrectChoiceId(payload)
  };
}

function createMathPlayerState<TPayload>(
  config: MathChoiceAdapterConfig<TPayload>,
  payload: TPayload
): MathPlayerState {
  return {
    ...createChoiceActivityState(createChoicePayload(config, payload)),
    lastAttemptedChoiceId: null
  };
}

function getEnabledChoiceIds<TPayload>(
  config: MathChoiceAdapterConfig<TPayload>,
  payload: TPayload,
  state: MathPlayerState
): readonly string[] {
  const choices = config.getChoices(payload);

  if (
    state.hintState.stage === "none" ||
    state.hintState.stage === "gentle-hint"
  ) {
    return choices.map(({ id }) => id);
  }

  const correctIds = choices
    .filter(({ id }) => config.isCorrectChoice(payload, id))
    .map(({ id }) => id);

  if (state.hintState.stage === "replay-demonstration") {
    return correctIds;
  }

  const firstDistractor = choices.find(
    ({ id }) => !config.isCorrectChoice(payload, id)
  );
  const enabledIds = new Set(correctIds);

  if (firstDistractor !== undefined) {
    enabledIds.add(firstDistractor.id);
  }

  return choices
    .map(({ id }) => id)
    .filter((id) => enabledIds.has(id));
}

function createNavigationPayload<TPayload>(
  config: MathChoiceAdapterConfig<TPayload>,
  payload: TPayload,
  state: MathPlayerState
): SingleChoicePayload {
  const enabledIds = new Set(getEnabledChoiceIds(config, payload, state));
  const choicePayload = createChoicePayload(config, payload);

  return {
    ...choicePayload,
    choices: choicePayload.choices.filter(({ id }) => enabledIds.has(id))
  };
}

function attemptChoice<TPayload>(
  config: MathChoiceAdapterConfig<TPayload>,
  payload: TPayload,
  state: MathPlayerState,
  choiceId: string,
  inputMethod: InputMethod
): MathPlayerState {
  if (state.status === "complete") {
    return state;
  }

  const enabledIds = getEnabledChoiceIds(config, payload, state);
  if (!enabledIds.includes(choiceId)) {
    return state;
  }

  const choicePayload = createChoicePayload(config, payload);
  const selectedState = reduceChoiceActivity(choicePayload, state, {
    type: "select-choice",
    choiceId,
    inputMethod
  });

  if (selectedState === state) {
    return state;
  }

  if (config.isCorrectChoice(payload, choiceId)) {
    return {
      ...completeChoiceMatchProgress(selectedState, 1),
      focusedChoiceId: selectedState.focusedChoiceId,
      lastAttemptedChoiceId: choiceId,
      lastInputMethod: selectedState.lastInputMethod,
      mode: selectedState.mode,
      selectedChoiceIds: selectedState.selectedChoiceIds
    };
  }

  const retryFeedback = createRetryFeedback(state.hintState).feedback;

  return {
    ...selectedState,
    feedback: retryFeedback,
    hintState: state.hintState,
    lastAttemptedChoiceId: choiceId,
    retryCount: state.retryCount + 1
  };
}

function reduceMathPlayer<TPayload>(
  config: MathChoiceAdapterConfig<TPayload>,
  payload: TPayload,
  state: MathPlayerState,
  action: MathPlayerAction
): MathPlayerState {
  switch (action.type) {
    case "select-choice":
      return attemptChoice(
        config,
        payload,
        state,
        action.choiceId,
        action.inputMethod
      );
    case "move-focus": {
      if (state.status === "complete") {
        return state;
      }

      const navigationPayload = createNavigationPayload(
        config,
        payload,
        state
      );
      const knownCurrentChoice = navigationPayload.choices.some(
        ({ id }) => id === action.currentChoiceId
      );
      const navigationState: MathPlayerState = {
        ...state,
        focusedChoiceId: knownCurrentChoice
          ? action.currentChoiceId
          : state.focusedChoiceId
      };
      const nextState = reduceChoiceActivity(
        navigationPayload,
        navigationState,
        {
          type: "move-focus",
          direction: action.direction
        }
      );

      return {
        ...nextState,
        lastAttemptedChoiceId: state.lastAttemptedChoiceId
      };
    }
    case "activate-focused":
      return state.focusedChoiceId === null
        ? state
        : attemptChoice(
            config,
            payload,
            state,
            state.focusedChoiceId,
            "keyboard"
          );
    case "request-hint": {
      if (state.status === "complete") {
        return state;
      }

      const hintedState: MathPlayerState = {
        ...state,
        feedback: null,
        hintState: advanceHintEscalation(state.hintState)
      };
      const enabledIds = getEnabledChoiceIds(config, payload, hintedState);

      return {
        ...hintedState,
        focusedChoiceId:
          hintedState.focusedChoiceId !== null &&
          enabledIds.includes(hintedState.focusedChoiceId)
            ? hintedState.focusedChoiceId
            : enabledIds[0] ?? null,
        selectedChoiceIds: hintedState.selectedChoiceIds.filter((id) =>
          enabledIds.includes(id)
        )
      };
    }
    case "replay-prompt": {
      const replayedState = reduceChoiceActivity(
        createChoicePayload(config, payload),
        state,
        { type: "replay-prompt" }
      );

      return {
        ...replayedState,
        lastAttemptedChoiceId: state.lastAttemptedChoiceId
      };
    }
    case "reset":
      return createMathPlayerState(config, payload);
  }
}

function getMetrics(state: MathPlayerState): ActivityCompletionMetrics {
  const completed = state.status === "complete";
  const score = calculateChoiceMatchCompletionScore(
    completed ? 1 : 0,
    state.retryCount,
    state.hintState.hintCount
  );

  return {
    completed,
    score: score.score,
    correctRate: score.correctRate,
    hintCount: score.hintCount,
    retryCount: score.retryCount
  };
}

function toRenderModel<TPayload>(
  config: MathChoiceAdapterConfig<TPayload>,
  payload: TPayload,
  state: MathPlayerState
): MathPlayerRenderModel {
  const completed = state.status === "complete";
  const enabledIds = new Set(getEnabledChoiceIds(config, payload, state));
  const selectedIds = new Set(state.selectedChoiceIds);
  let hint: MathPlayerHintModel | null = null;

  if (!completed && state.feedback?.kind === "retry") {
    const cue = config.getRetryCue(payload);

    hint = {
      audioPath: cue.audioPath,
      kind: "retry",
      playbackRequest: state.hintState.hintCount + state.retryCount,
      stage: state.feedback.hintStage,
      text: cue.text
    };
  } else if (!completed && state.hintState.stage !== "none") {
    const cue = config.getHintCue(payload, state.hintState.stage);

    hint = {
      audioPath: cue.audioPath,
      kind: "hint",
      playbackRequest: state.hintState.hintCount + state.retryCount,
      stage: state.hintState.stage,
      text: cue.text
    };
  }

  return {
    activityType: config.activityType,
    choicePrompt: config.getChoicePrompt(payload),
    choices: config.getChoices(payload).map((choice) => ({
      ...choice,
      disabled: completed || !enabledIds.has(choice.id),
      focused: state.focusedChoiceId === choice.id,
      selected: selectedIds.has(choice.id)
    })),
    completed,
    hint,
    result: completed ? config.getResult(payload) : null,
    visual: config.createVisual(payload, state)
  };
}

function createMathChoiceAdapter<TPayload>(
  config: MathChoiceAdapterConfig<TPayload>
): ActivityAdapter<
  TPayload,
  MathPlayerState,
  MathPlayerAction,
  MathPlayerRenderModel
> {
  return {
    activityType: config.activityType,
    parsePayload: config.parsePayload,
    createInitialState: (payload) => createMathPlayerState(config, payload),
    reduce: (state, payload, action) =>
      reduceMathPlayer(config, payload, state, action),
    getMetrics: (state) => getMetrics(state),
    toRenderModel: (state, payload) =>
      toRenderModel(config, payload, state)
  };
}

function parseNumberTrainPayload(value: unknown): NumberTrainPayload {
  return ACTIVITY_PAYLOAD_SCHEMAS.math["number-train"].parse(value);
}

function getNumberTrainExpectedCount(payload: NumberTrainPayload): number {
  if (payload.mode === "match") {
    return payload.targetCount;
  }

  const counts = payload.choices.map(({ count }) => count);
  return payload.relation === "most"
    ? Math.max(...counts)
    : Math.min(...counts);
}

export const NUMBER_TRAIN_PLAYER_ADAPTER =
  createMathChoiceAdapter<NumberTrainPayload>({
    activityType: "number-train",
    parsePayload: parseNumberTrainPayload,
    getCanonicalCorrectChoiceId: (payload) => payload.correctChoiceId,
    getChoices: (payload) =>
      payload.choices.map((choice) => ({
        id: choice.id,
        label: String(choice.count) + "颗星星",
        display: {
          kind: "stars",
          count: choice.count
        }
      })),
    isCorrectChoice: (payload, choiceId) => {
      const choice = payload.choices.find(({ id }) => id === choiceId);
      return choice?.count === getNumberTrainExpectedCount(payload);
    },
    getChoicePrompt: (payload) => {
      if (payload.mode === "match") {
        return "哪节车厢和站台一样多？";
      }
      return payload.relation === "most"
        ? "哪节车厢的星星最多？"
        : "哪节车厢的星星最少？";
    },
    getRetryCue: getNumberTrainRetryCue,
    getHintCue: getNumberTrainHintCue,
    createVisual: (payload) =>
      payload.mode === "match"
        ? {
            kind: "number-train",
            mode: "match",
            relation: null,
            targetCount: payload.targetCount
          }
        : {
            kind: "number-train",
            mode: "compare",
            relation: payload.relation,
            targetCount: null
          },
    getResult: (payload) => ({
      heading: "星星坐稳啦",
      message:
        "装着" +
        String(getNumberTrainExpectedCount(payload)) +
        "颗星星的车厢找到啦。"
    })
  });

function parseForestMarketPayload(value: unknown): ForestMarketPayload {
  return ACTIVITY_PAYLOAD_SCHEMAS.math["forest-market"].parse(value);
}

function getForestMarketExpectedCount(payload: ForestMarketPayload): number {
  return payload.operation === "add"
    ? payload.startingCount + payload.changeCount
    : payload.startingCount - payload.changeCount;
}

export const FOREST_MARKET_PLAYER_ADAPTER =
  createMathChoiceAdapter<ForestMarketPayload>({
    activityType: "forest-market",
    parsePayload: parseForestMarketPayload,
    getCanonicalCorrectChoiceId: (payload) => payload.correctChoiceId,
    getChoices: (payload) => {
      const itemLabel = getMarketItemLabel(payload.itemId);
      const itemUnit = getMarketItemUnit(payload.itemId);

      return payload.choices.map((choice) => ({
        id: choice.id,
        label: String(choice.count) + itemUnit + itemLabel,
        display: {
          kind: "market-item",
          count: choice.count,
          itemId: payload.itemId
        }
      }));
    },
    isCorrectChoice: (payload, choiceId) => {
      const choice = payload.choices.find(({ id }) => id === choiceId);
      return choice?.count === getForestMarketExpectedCount(payload);
    },
    getChoicePrompt: () => "现在篮子里有多少？",
    getRetryCue: getForestMarketRetryCue,
    getHintCue: getForestMarketHintCue,
    createVisual: (payload) => ({
      kind: "forest-market",
      changeCount: payload.changeCount,
      itemId: payload.itemId,
      itemLabel: getMarketItemLabel(payload.itemId),
      itemUnit: getMarketItemUnit(payload.itemId),
      operation: payload.operation,
      startingCount: payload.startingCount
    }),
    getResult: (payload) => ({
      heading: "篮子数清啦",
      message:
        "篮子里现在刚好有" +
        String(getForestMarketExpectedCount(payload)) +
        getMarketItemUnit(payload.itemId) +
        getMarketItemLabel(payload.itemId) +
        "。"
    })
  });

const patternModeSchema = z.object({
  mode: z.enum(["sequence", "route"])
});

function parsePatternBridgePayload(value: unknown): PatternBridgePayload {
  const { mode } = patternModeSchema.parse(value);

  if (mode === "sequence") {
    return mathPatternSequencePayloadSchema.parse(value);
  }

  return ACTIVITY_PAYLOAD_SCHEMAS.math["pattern-bridge"].parse(value);
}

function getPatternChoice(
  payload: PatternBridgePayload,
  choiceId: string
) {
  return payload.choices.find(({ id }) => id === choiceId);
}

function getExpectedPatternToken(
  payload: Extract<PatternBridgePayload, { mode: "sequence" }>
): PatternToken | null {
  const emptyIndex = payload.sequence.findIndex((token) => token === null);

  if (emptyIndex < 0) {
    return null;
  }

  return payload.repeatUnit[emptyIndex % payload.repeatUnit.length] ?? null;
}

function getRouteEndpoint(
  payload: Extract<PatternBridgePayload, { mode: "route" }>
): GridPosition {
  return payload.moves.reduce<GridPosition>((position, move) => {
    switch (move) {
      case "up":
        return { column: position.column, row: position.row - 1 };
      case "right":
        return { column: position.column + 1, row: position.row };
      case "down":
        return { column: position.column, row: position.row + 1 };
      case "left":
        return { column: position.column - 1, row: position.row };
    }
  }, payload.start);
}

function positionsMatch(
  first: GridPosition,
  second: GridPosition
): boolean {
  return first.column === second.column && first.row === second.row;
}

function isCorrectPatternChoice(
  payload: PatternBridgePayload,
  choiceId: string
): boolean {
  const choice = getPatternChoice(payload, choiceId);
  if (choice === undefined) {
    return false;
  }

  if (payload.mode === "sequence") {
    return (
      "token" in choice && choice.token === getExpectedPatternToken(payload)
    );
  }

  return (
    "position" in choice &&
    positionsMatch(choice.position, getRouteEndpoint(payload))
  );
}

function getResolvedPatternSequence(
  payload: Extract<PatternBridgePayload, { mode: "sequence" }>
): readonly (PatternToken | null)[] {
  const expectedToken = getExpectedPatternToken(payload);

  return payload.sequence.map((token) => token ?? expectedToken);
}

export const PATTERN_BRIDGE_PLAYER_ADAPTER =
  createMathChoiceAdapter<PatternBridgePayload>({
    activityType: "pattern-bridge",
    parsePayload: parsePatternBridgePayload,
    getCanonicalCorrectChoiceId: (payload) => payload.correctChoiceId,
    getChoices: (payload) => {
      if (payload.mode === "sequence") {
        return payload.choices.map((choice) => ({
          id: choice.id,
          label: MATH_PATTERN_TOKEN_LABELS[choice.token],
          display: {
            kind: "pattern-token",
            token: choice.token
          }
        }));
      }

      return payload.choices.map((choice) => ({
        id: choice.id,
        label:
          "第" +
          String(choice.position.row + 1) +
          "行第" +
          String(choice.position.column + 1) +
          "列",
        display: {
          kind: "route-position",
          position: choice.position
        }
      }));
    },
    isCorrectChoice: isCorrectPatternChoice,
    getChoicePrompt: (payload) =>
      payload.mode === "sequence"
        ? "空位该轮到哪个图案？"
        : "小船最后会停在哪一格？",
    getRetryCue: getPatternRetryCue,
    getHintCue: getPatternHintCue,
    createVisual: (payload, state) => {
      if (payload.mode === "sequence") {
        return {
          kind: "pattern-sequence",
          tokens:
            state.status === "complete"
              ? getResolvedPatternSequence(payload)
              : payload.sequence
        };
      }

      return {
        kind: "pattern-route",
        columns: payload.columns,
        rows: payload.rows,
        start: payload.start,
        moves: payload.moves
      };
    },
    getResult: (payload) => {
      if (payload.mode === "sequence") {
        return {
          heading: "图案小桥接好啦",
          message:
            getResolvedPatternSequence(payload)
              .flatMap((token) =>
                token === null ? [] : [MATH_PATTERN_TOKEN_LABELS[token]]
              )
              .join("、") + "，规律补完整了。"
        };
      }

      return {
        heading: "路线走通啦",
        message: "小船按箭头的顺序走到了终点。"
      };
    }
  });
